import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { PFieldAbstractComponent } from '../p-field-abstract';

export enum TimePart {
    Hours,
    Minutes
}

@Component({
    selector: 'p-field-time',
    template: `
        <p-field
            [isMandatory]="isMandatory"
            [label]="label"
            [labelTooltip]="labelTooltip"
            [isReadOnly]="isReadOnly"
            [isDisable]="isDisabled"
            [shouldTransformLabelTextCase]="shouldTransformLabelTextCase"
        >
            <div slot="editMode">
                <div class="time">
                    <p-field-single-select
                        [list]="hoursList"
                        customClasses="pem-style"
                        menuCustomClasses="time-width"
                        [isMandatory]="isMandatory"
                        [isReadOnly]="isReadOnly"
                        [labelTooltip]="labelTooltip"
                        [valueTooltip]="valueTooltip"
                        [value]="hours"
                        (valueChange)="recalculateValue($event, timePart.Hours)"
                        [attr.automation-id]="'field-single_select-hours'"
                    >
                    </p-field-single-select>
                    <span class="spanPadding">:</span>
                    <p-field-single-select
                        [list]="minutesList"
                        customClasses="pem-style"
                        menuCustomClasses="time-width"
                        [isMandatory]="isMandatory"
                        [isReadOnly]="isReadOnly"
                        [labelTooltip]="labelTooltip"
                        [valueTooltip]="valueTooltip"
                        [value]="minutes"
                        (valueChange)="recalculateValue($event, timePart.Minutes)"
                        [attr.automation-id]="'field-single_select-minutes'"
                    >
                    </p-field-single-select>
                    <span class="spanPadding">{{ 'paExecutionScheduling.Fields.localTime' | translate }}</span>
                    <span class="spanPadding">{{ getLocalTimeText() }}</span>
                </div>
            </div>

            <div slot="readMode">
                <p-field-text
                    [isDisabled]="true"
                    class="p-field-time-hours"
                    [ngModel]="hours + ' : ' + minutes + '(UTC)'"
                    [ngModelOptions]="{ standalone: true }"
                    [hasInputListener]="true"
                ></p-field-text>
            </div>
        </p-field>
    `,
    styleUrls: ['./p-field-time.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: PFieldTimeComponent
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'pFieldTime'
})
export class PFieldTimeComponent extends PFieldAbstractComponent<number> implements AfterViewInit {
    @Input() label?: string;
    @Input() isMandatory = false;

    protected timePart = TimePart;

    protected hours: number = 0;
    protected minutes: number = 0;

    protected recalculateValue(event: number, changedTimePart: TimePart): void {
        this.markAsTouched();

        const currentMinutes = this.minutes;
        const currentHours = this.hours;
        if (!this.isReadOnly) {
            if (changedTimePart == TimePart.Hours) {
                this.value = event * 60 + currentMinutes; //05:30=330
            } else {
                this.value = event + currentHours * 60; //05:35
            }
            this._onChange(this?.value);
        }
        this.hours = this.getHours(this.value);
        this.minutes = this.getMinutes(this.value);
    }

    constructor(cd: ChangeDetectorRef) {
        super(cd);
    }

    ngAfterViewInit(): void {
        this.hours = this.getHours(this.value);
        this.minutes = this.getMinutes(this.value);
        this.cd.detectChanges();
    }

    private createTimeList(count: number): { id: number; name: string }[] {
        let result = [];
        for (let i = 0; i < count; i++) {
            result.push({ id: i, name: i < 10 ? '0' + i : '' + i });
        }
        return result;
    }

    protected hoursList: { id: number; name: string }[] = this.createTimeList(24);

    protected minutesList: { id: number; name: string }[] = this.createTimeList(60);

    private getHours(time: number | undefined): number {
        return time ? Math.floor(time / 60) : 0;
    }

    private getMinutes(time: number | undefined): number {
        return time ? time % 60 : 0;
    }

    private formatAutomationId(automationIdPrefix: string, keyStr: string): string {
        return automationIdPrefix + keyStr?.replace(/ /g, '_').toLowerCase();
    }

    getLocalTimeText() {
        const timeZoneOffset = -new Date().getTimezoneOffset() / 60;
        const offsetStr = timeZoneOffset > 0 ? '+' + timeZoneOffset : timeZoneOffset;
        return '(GMT' + offsetStr + ')';
    }

    override writeValue(time: number): void {
        this.hours = this.getHours(time);
        this.minutes = this.getMinutes(time);
        super.writeValue(time);
    }
}
